import { ROLES } from '../constants/roles';
import { getManageableHouseIds, type HouseViewer } from './houseAccess';
import { staffBelongsToHouse } from './staffAssignment';
import { isGhostUser } from './userVisibility';

export interface MessageParticipant extends HouseViewer {
  id?: string;
  name?: string;
  email?: string;
}

/** Sentinel house id: subscribe to the full listable user directory (god only). */
export const PLATFORM_MESSAGING_ROSTER = '*';

export const EXTERNAL_RECIPIENT_PREFIX = 'external:';

export function isPlatformWideMessagingRoster(
  sender: MessageParticipant | null | undefined,
): boolean {
  return sender?.role === ROLES.GOD;
}

export function canSendExternalEmail(
  sender: MessageParticipant | null | undefined,
): boolean {
  return sender?.role === ROLES.GOD;
}

export function isExternalRecipientId(id?: string | null): boolean {
  return Boolean(id && id.startsWith(EXTERNAL_RECIPIENT_PREFIX));
}

export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());
}

export function externalRecipientFromEmail(email: string): MessageParticipant {
  const normalized = String(email ?? '').trim().toLowerCase();
  return {
    id: `${EXTERNAL_RECIPIENT_PREFIX}${normalized}`,
    name: normalized,
    email: normalized,
  };
}

/** Houses whose members may appear in the compose recipient search. */
export function messageRosterHouseIds(
  sender: MessageParticipant | null | undefined,
): string[] {
  if (!sender) return [];
  if (isPlatformWideMessagingRoster(sender)) return [PLATFORM_MESSAGING_ROSTER];
  return getManageableHouseIds(sender);
}

function recipientInHouses(
  recipient: MessageParticipant | null | undefined,
  houseIds: string[],
): boolean {
  if (!recipient || houseIds.length === 0) return false;
  if (houseIds.includes(PLATFORM_MESSAGING_ROSTER)) return true;
  return houseIds.some((houseId) => staffBelongsToHouse(recipient, houseId));
}

export function canSendDirectMessage(
  sender: MessageParticipant | null | undefined,
  recipient: MessageParticipant | null | undefined,
): boolean {
  if (!sender?.id || !recipient?.id) return false;
  if (sender.id === recipient.id) return false;

  if (isExternalRecipientId(recipient.id)) {
    return canSendExternalEmail(sender) && looksLikeEmail(recipient.email ?? '');
  }

  if (isGhostUser(recipient)) return false;

  if (sender.role === ROLES.GOD) return true;

  const senderHouses = getManageableHouseIds(sender);
  return recipientInHouses(recipient, senderHouses);
}

export function filterMessageRecipients(
  sender: MessageParticipant | null | undefined,
  users: MessageParticipant[],
): MessageParticipant[] {
  if (isPlatformWideMessagingRoster(sender)) {
    return users.filter((user) => canSendDirectMessage(sender, user));
  }

  const rosterHouses = messageRosterHouseIds(sender);
  if (rosterHouses.length === 0) return [];

  return users.filter((user) => {
    if (!canSendDirectMessage(sender, user)) return false;
    return recipientInHouses(user, rosterHouses);
  });
}
